import * as z from "zod";
import Papa from "papaparse";
import * as XLSX from "xlsx";

// Holdings row schema
export const holdingsRowSchema = z.object({
  fund_name: z.string().min(1, "Fund name is required"),
  fund_type: z.string().optional(),
  commitment: z.number().min(0, "Commitment must be positive"),
  paid_in: z.number().optional(),
  nav: z.number().optional(),
  distributions: z.number().optional(),
  as_of_date: z.string().min(1, "As-of date is required"),
  currency: z.string().default("USD"),
  notes: z.string().optional(),
});

export type HoldingsRow = z.infer<typeof holdingsRowSchema>;

// Transactions row schema
export const transactionsRowSchema = z.object({
  date: z.string().min(1, "Date is required"),
  type: z.enum(["call", "distribution", "fee", "other"]),
  fund_name: z.string().min(1, "Fund name is required"),
  amount: z.number(),
  description: z.string().optional(),
});

export type TransactionsRow = z.infer<typeof transactionsRowSchema>;

// Validation result
export interface ValidationResult<T> {
  valid: boolean;
  errors: Array<{
    row: number;
    field: string;
    message: string;
  }>;
  data: T[];
}

// Parse result
export interface ParseResult<T> {
  success: boolean;
  data: T[];
  errors: string[];
  headers: string[];
}

// Column mapping configuration
export interface ColumnMapping {
  [key: string]: string | null;
}

// Template definitions for download
export const HOLDINGS_TEMPLATE_HEADERS = [
  "fund_name",
  "fund_type",
  "commitment",
  "paid_in",
  "nav",
  "distributions",
  "as_of_date",
  "currency",
  "notes",
];

export const HOLDINGS_TEMPLATE_SAMPLE = [
  {
    fund_name: "Sequoia Capital Fund XX",
    fund_type: "private_fund",
    commitment: 5000000,
    paid_in: 3500000,
    nav: 4200000,
    distributions: 500000,
    as_of_date: "2025-12-31",
    currency: "USD",
    notes: "Vintage 2023",
  },
  {
    fund_name: "Blackstone Real Estate Partners X",
    fund_type: "private_fund",
    commitment: 10000000,
    paid_in: 7000000,
    nav: 8500000,
    distributions: 1200000,
    as_of_date: "2025-12-31",
    currency: "USD",
    notes: "Real estate allocation",
  },
];

export const TRANSACTIONS_TEMPLATE_HEADERS = [
  "date",
  "type",
  "fund_name",
  "amount",
  "description",
];

export const TRANSACTIONS_TEMPLATE_SAMPLE = [
  {
    date: "2025-03-15",
    type: "call",
    fund_name: "Sequoia Capital Fund XX",
    amount: 500000,
    description: "Q1 2025 Capital Call",
  },
  {
    date: "2025-06-30",
    type: "distribution",
    fund_name: "Blackstone Real Estate Partners X",
    amount: 250000,
    description: "Q2 2025 Distribution",
  },
];

// Parse number from various formats
function parseNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    // Remove currency symbols, commas, and spaces
    const cleaned = value.replace(/[$€£¥,\s]/g, "").trim();
    if (cleaned === "") return undefined;
    const num = parseFloat(cleaned);
    return isNaN(num) ? undefined : num;
  }
  return undefined;
}

// Parse date from various formats
function parseDate(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") {
    // Try to parse and standardize date format
    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0];
    }
    return value;
  }
  if (typeof value === "number") {
    // Excel serial date
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      return `${date.y}-${String(date.m).padStart(2, "0")}-${String(date.d).padStart(2, "0")}`;
    }
  }
  return String(value);
}

// Normalize header names
function normalizeHeader(header: string): string {
  return header
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

// Map raw data to holdings row
function mapToHoldingsRow(row: Record<string, unknown>, mapping: ColumnMapping): Partial<HoldingsRow> {
  return {
    fund_name: String(row[mapping.fund_name || "fund_name"] || ""),
    fund_type: row[mapping.fund_type || "fund_type"] ? String(row[mapping.fund_type || "fund_type"]) : undefined,
    commitment: parseNumber(row[mapping.commitment || "commitment"]) ?? 0,
    paid_in: parseNumber(row[mapping.paid_in || "paid_in"]),
    nav: parseNumber(row[mapping.nav || "nav"]),
    distributions: parseNumber(row[mapping.distributions || "distributions"]),
    as_of_date: parseDate(row[mapping.as_of_date || "as_of_date"]),
    currency: String(row[mapping.currency || "currency"] || "USD"),
    notes: row[mapping.notes || "notes"] ? String(row[mapping.notes || "notes"]) : undefined,
  };
}

// Map raw data to transactions row
function mapToTransactionsRow(row: Record<string, unknown>, mapping: ColumnMapping): Partial<TransactionsRow> {
  const typeValue = String(row[mapping.type || "type"] || "").toLowerCase();
  const validTypes = ["call", "distribution", "fee", "other"];
  const type = validTypes.includes(typeValue) ? typeValue : "other";

  return {
    date: parseDate(row[mapping.date || "date"]),
    type: type as TransactionsRow["type"],
    fund_name: String(row[mapping.fund_name || "fund_name"] || ""),
    amount: parseNumber(row[mapping.amount || "amount"]) ?? 0,
    description: row[mapping.description || "description"] ? String(row[mapping.description || "description"]) : undefined,
  };
}

// Parse CSV content for holdings
export function parseHoldingsCSV(
  content: string,
  mapping?: ColumnMapping
): ParseResult<HoldingsRow> {
  const errors: string[] = [];
  const result = Papa.parse<Record<string, unknown>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: normalizeHeader,
  });

  if (result.errors.length > 0) {
    result.errors.forEach((err) => {
      errors.push(`Row ${err.row}: ${err.message}`);
    });
  }

  const headers = result.meta.fields || [];
  const defaultMapping: ColumnMapping = {};
  headers.forEach((h) => {
    defaultMapping[h] = h;
  });

  const finalMapping = { ...defaultMapping, ...mapping };
  const data: HoldingsRow[] = [];

  result.data.forEach((row, idx) => {
    try {
      const mapped = mapToHoldingsRow(row, finalMapping);
      const validated = holdingsRowSchema.parse(mapped);
      data.push(validated);
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((e) => {
          errors.push(`Row ${idx + 2}: ${e.path.join(".")} - ${e.message}`);
        });
      }
    }
  });

  return {
    success: errors.length === 0,
    data,
    errors,
    headers,
  };
}

// Parse Excel file for holdings
export async function parseHoldingsExcel(
  file: File,
  sheetIndex = 0,
  mapping?: ColumnMapping
): Promise<ParseResult<HoldingsRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[sheetIndex];
        const sheet = workbook.Sheets[sheetName];

        // Convert to JSON with raw values
        const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          raw: false,
          defval: "",
        });

        // Normalize headers
        const normalizedData = jsonData.map((row) => {
          const normalized: Record<string, unknown> = {};
          Object.keys(row).forEach((key) => {
            normalized[normalizeHeader(key)] = row[key];
          });
          return normalized;
        });

        // Get headers
        const headers = Object.keys(normalizedData[0] || {});

        const defaultMapping: ColumnMapping = {};
        headers.forEach((h) => {
          defaultMapping[h] = h;
        });

        const finalMapping = { ...defaultMapping, ...mapping };
        const errors: string[] = [];
        const parsedData: HoldingsRow[] = [];

        normalizedData.forEach((row, idx) => {
          try {
            const mapped = mapToHoldingsRow(row, finalMapping);
            const validated = holdingsRowSchema.parse(mapped);
            parsedData.push(validated);
          } catch (error) {
            if (error instanceof z.ZodError) {
              error.errors.forEach((e) => {
                errors.push(`Row ${idx + 2}: ${e.path.join(".")} - ${e.message}`);
              });
            }
          }
        });

        resolve({
          success: errors.length === 0,
          data: parsedData,
          errors,
          headers,
        });
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsArrayBuffer(file);
  });
}

// Parse CSV content for transactions
export function parseTransactionsCSV(
  content: string,
  mapping?: ColumnMapping
): ParseResult<TransactionsRow> {
  const errors: string[] = [];
  const result = Papa.parse<Record<string, unknown>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: normalizeHeader,
  });

  if (result.errors.length > 0) {
    result.errors.forEach((err) => {
      errors.push(`Row ${err.row}: ${err.message}`);
    });
  }

  const headers = result.meta.fields || [];
  const defaultMapping: ColumnMapping = {};
  headers.forEach((h) => {
    defaultMapping[h] = h;
  });

  const finalMapping = { ...defaultMapping, ...mapping };
  const data: TransactionsRow[] = [];

  result.data.forEach((row, idx) => {
    try {
      const mapped = mapToTransactionsRow(row, finalMapping);
      const validated = transactionsRowSchema.parse(mapped);
      data.push(validated);
    } catch (error) {
      if (error instanceof z.ZodError) {
        error.errors.forEach((e) => {
          errors.push(`Row ${idx + 2}: ${e.path.join(".")} - ${e.message}`);
        });
      }
    }
  });

  return {
    success: errors.length === 0,
    data,
    errors,
    headers,
  };
}

// Parse Excel file for transactions
export async function parseTransactionsExcel(
  file: File,
  sheetIndex = 0,
  mapping?: ColumnMapping
): Promise<ParseResult<TransactionsRow>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[sheetIndex];
        const sheet = workbook.Sheets[sheetName];

        const jsonData = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          raw: false,
          defval: "",
        });

        const normalizedData = jsonData.map((row) => {
          const normalized: Record<string, unknown> = {};
          Object.keys(row).forEach((key) => {
            normalized[normalizeHeader(key)] = row[key];
          });
          return normalized;
        });

        const headers = Object.keys(normalizedData[0] || {});

        const defaultMapping: ColumnMapping = {};
        headers.forEach((h) => {
          defaultMapping[h] = h;
        });

        const finalMapping = { ...defaultMapping, ...mapping };
        const errors: string[] = [];
        const parsedData: TransactionsRow[] = [];

        normalizedData.forEach((row, idx) => {
          try {
            const mapped = mapToTransactionsRow(row, finalMapping);
            const validated = transactionsRowSchema.parse(mapped);
            parsedData.push(validated);
          } catch (error) {
            if (error instanceof z.ZodError) {
              error.errors.forEach((e) => {
                errors.push(`Row ${idx + 2}: ${e.path.join(".")} - ${e.message}`);
              });
            }
          }
        });

        resolve({
          success: errors.length === 0,
          data: parsedData,
          errors,
          headers,
        });
      } catch (error) {
        reject(new Error(`Failed to parse Excel file: ${error}`));
      }
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsArrayBuffer(file);
  });
}

// Generate CSV template content
export function generateHoldingsCSV(): string {
  const headers = HOLDINGS_TEMPLATE_HEADERS.join(",");
  const rows = HOLDINGS_TEMPLATE_SAMPLE.map((row) =>
    HOLDINGS_TEMPLATE_HEADERS.map((h) => {
      const value = row[h as keyof typeof row];
      if (typeof value === "string" && value.includes(",")) {
        return `"${value}"`;
      }
      return value ?? "";
    }).join(",")
  );
  return [headers, ...rows].join("\n");
}

export function generateTransactionsCSV(): string {
  const headers = TRANSACTIONS_TEMPLATE_HEADERS.join(",");
  const rows = TRANSACTIONS_TEMPLATE_SAMPLE.map((row) =>
    TRANSACTIONS_TEMPLATE_HEADERS.map((h) => {
      const value = row[h as keyof typeof row];
      if (typeof value === "string" && value.includes(",")) {
        return `"${value}"`;
      }
      return value ?? "";
    }).join(",")
  );
  return [headers, ...rows].join("\n");
}

// Generate Excel template
export function generateHoldingsExcel(): ArrayBuffer {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(HOLDINGS_TEMPLATE_SAMPLE);
  XLSX.utils.book_append_sheet(wb, ws, "Holdings");
  return XLSX.write(wb, { type: "array", bookType: "xlsx" });
}

export function generateTransactionsExcel(): ArrayBuffer {
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(TRANSACTIONS_TEMPLATE_SAMPLE);
  XLSX.utils.book_append_sheet(wb, ws, "Transactions");
  return XLSX.write(wb, { type: "array", bookType: "xlsx" });
}

// Download helper
export function downloadFile(content: string | ArrayBuffer, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadHoldingsCSV() {
  downloadFile(generateHoldingsCSV(), "holdings_template.csv", "text/csv");
}

export function downloadHoldingsExcel() {
  downloadFile(
    generateHoldingsExcel(),
    "holdings_template.xlsx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}

export function downloadTransactionsCSV() {
  downloadFile(generateTransactionsCSV(), "transactions_template.csv", "text/csv");
}

export function downloadTransactionsExcel() {
  downloadFile(
    generateTransactionsExcel(),
    "transactions_template.xlsx",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
}
